import React, { useState, useEffect } from 'react';
import { CalendarDays, GraduationCap } from 'lucide-react';

const CountdownTimer = ({ targetDate, title, message }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Function to format title with superscript ordinal
  const formatTitleWithSuperscript = (title) => {
    // Match patterns like "1st", "2nd", "3rd", "4th", "5th", etc.
    const regex = /(\d+)(st|nd|rd|th)/i;
    const match = title.match(regex);

    if (match) {
      const number = match[1];
      const suffix = match[2];
      const before = title.substring(0, match.index);
      const after = title.substring(match.index + match[0].length);

      return (
        <>
          {before}
          {number}
          <sup className="text-xs align-super">{suffix}</sup>
          {after}
        </>
      );
    }
    return title;
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  const formatNumber = (num) => {
    return num.toString().padStart(2, '0');
  };

  const formattedTitle = formatTitleWithSuperscript(title);

  const units = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hours', value: timeLeft.hours },
    { label: 'Mins', value: timeLeft.minutes },
    { label: 'Secs', value: timeLeft.seconds },
  ];

  const isCeremonyDay = units.every((unit) => unit.value === 0);

  return (
    <section className="section bg-white" aria-label="Graduation countdown">
      <div className="container-page">
        <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-card">
          {/* Title band */}
          <div className="flex flex-col items-center gap-1.5 bg-brand-700 px-6 py-5 text-center">
            <span className="eyebrow bg-white/10 text-gold-300">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
              Countdown
            </span>
            <h2 className="mt-1 text-xl font-bold text-white md:text-2xl">
              {formattedTitle}
            </h2>
            {message ? <p className="text-sm text-white/70">{message}</p> : null}
          </div>

          {/* Digits */}
          <div className="p-5 md:p-8">
            <div className="grid grid-cols-4 gap-2 md:gap-4">
              {units.map((unit) => (
                <div key={unit.label} className="text-center">
                  <div className="rounded-xl border border-ink-200 bg-ink-50 px-2 py-3 md:py-5">
                    <div className="text-2xl font-bold tabular-nums text-brand-700 md:text-4xl">
                      {formatNumber(unit.value)}
                    </div>
                    <div className="mt-1 text-2xs font-semibold uppercase tracking-widest text-ink-500 md:text-xs">
                      {unit.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {isCeremonyDay && (
              <div
                role="status"
                className="mt-6 flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800"
              >
                <GraduationCap className="h-4 w-4" aria-hidden="true" />
                The graduation day is here!
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CountdownTimer;
